// Real Idukki customer storefronts + their production widget captures.
// Single source of truth for the homepage "Live on stores" band, the
// /videos watch pages and the video sitemap entries. Honest by design:
// real brands, real screen recordings from the live stores, no invented
// metrics. Every video listed here has a poster frame in
// public/brand/customer-screenshots/posters/ (same basename, .webp).

export type StorefrontVideo = {
  /** Public path of the webm capture. */
  src: string
  /** Poster frame (public path). Also the sitemap/VideoObject thumbnail. */
  poster: string
  /** Rounded duration in seconds (for VideoObject ISO-8601 duration). */
  durationSec: number
  /** Human title for this specific clip, e.g. "Homepage reel carousel". */
  title: string
}

export type Storefront = {
  /** URL slug for the /videos/[slug] watch page. */
  slug: string
  name: string
  url: string
  industry: string
  usesIdukkiFor: string
  /** Short widget-type chip rendered on the card (e.g. "Shoppable wall"). */
  widgetType: string
  /** First-letter fallback when no logo asset exists. */
  initial: string
  /** Real brand logo from public/brand/customer-logos/, when we have it. */
  logo?: string
  /** Production widget captures, primary clip first. */
  videos: StorefrontVideo[]
  /** Previous platform this customer migrated from (e.g. 'Bazaarvoice'). */
  replacedFrom?: string
}

// Captures were recorded from the live storefronts and committed on this
// date; used as the VideoObject uploadDate.
export const STOREFRONT_VIDEOS_PUBLISHED = '2026-07-27'

const P = '/brand/customer-screenshots'
const poster = (file: string) => `${P}/posters/${file.replace(/\.webm$/, '.webp')}`
const clip = (file: string, durationSec: number, title: string): StorefrontVideo => ({
  src: `${P}/${file}`,
  poster: poster(file),
  durationSec,
  title,
})

export const STOREFRONTS: Storefront[] = [
  {
    slug: 'thecaistore',
    name: 'thecaistore.com',
    url: 'https://thecaistore.com/pages/shopbylooks-inspiration',
    industry: 'Fashion · Footwear',
    usesIdukkiFor: 'Shop-by-looks UGC wall + shoppable reels modal',
    widgetType: 'Shoppable wall',
    initial: 'C',
    logo: '/brand/customer-logos/caistore.jpg',
    videos: [clip('thecaistore.webm', 11, 'Shop-by-looks inspiration wall')],
  },
  {
    slug: 'theater',
    name: 'theater.xyz',
    url: 'https://theater.xyz/pages/women-bags',
    industry: 'Fashion · DTC',
    usesIdukkiFor: 'Hashtag-wall hero (#mytheater) + product-detail modal',
    widgetType: 'Hashtag wall',
    initial: 'T',
    logo: '/brand/customer-logos/theater.png',
    videos: [clip('theather-pdp.webm', 4, '#mytheater hashtag wall + product modal')],
  },
  {
    slug: 'mallewear',
    name: 'mallewear.com',
    url: 'https://www.mallewear.com/products/essentials-malle-full-bloom-sleeveless-boxy-top',
    industry: 'Fashion · Streetwear',
    usesIdukkiFor: 'Circle-stories widget on every PDP',
    widgetType: 'PDP stories',
    initial: 'M',
    logo: '/brand/customer-logos/mallewear.png',
    videos: [clip('malleware.webm', 11, 'Circle-stories widget on the product page')],
  },
  {
    slug: 'fixmycurls',
    name: 'fixmycurls.com',
    url: 'https://www.fixmycurls.com',
    industry: 'Beauty · Hair care',
    usesIdukkiFor: '"Most loved IRL" shoppable reel carousel',
    widgetType: 'Reel carousel',
    initial: 'F',
    logo: '/brand/customer-logos/fixmycurls.webp',
    videos: [
      clip('fix-my-curls-homepage.webm', 7, '"Most loved IRL" homepage reel carousel'),
      clip('fix-my-curls-pdp.webm', 4, 'Shoppable reels on the product page'),
    ],
  },
  {
    slug: 'leroymerlin',
    name: 'leroymerlin.co.za',
    url: 'https://www.leroymerlin.co.za/inspirations',
    industry: 'Home · Hardware',
    usesIdukkiFor: 'SNAP-TAG-SHARE inspiration wall + competition entries',
    widgetType: 'Inspiration wall',
    initial: 'L',
    logo: '/brand/customer-logos/leroymerlin.svg',
    videos: [clip('leory-merlin-inspiration.webm', 6, 'SNAP-TAG-SHARE inspiration wall')],
    replacedFrom: 'Bazaarvoice',
  },
  {
    slug: 'ritualistic',
    name: 'ritualistic.in',
    url: 'https://www.ritualistic.in/products/hawan-samagri-sacred-monthly-ritual-kit-7-day-ritual',
    industry: 'Home · Indian Rituals',
    usesIdukkiFor: 'UGC video feed showcasing ritual products in use',
    widgetType: 'Reel carousel',
    initial: 'R',
    logo: '/brand/customer-logos/ritualistic.png',
    videos: [
      clip('ritualistic-pdp.webm', 3, 'UGC video feed on the product page'),
      clip('ritualistic-miniframe.webm', 7, 'Mini-frame video widget'),
    ],
  },
  {
    slug: 'amoshi',
    name: 'amoshi.in',
    url: 'https://amoshi.in/products/daisy-co-ord-set-white',
    industry: 'Fashion · Womenswear',
    usesIdukkiFor: 'Shoppable UGC gallery on homepage + PDP',
    widgetType: 'Reel carousel',
    initial: 'A',
    logo: '/brand/customer-logos/amoshi.png',
    videos: [
      clip('Amoshi-homepage.webm', 5, 'Shoppable UGC gallery on the homepage'),
      clip('amoshi-pdp.webm', 2, 'UGC gallery on the product page'),
    ],
  },
  {
    slug: 'cossetclothing',
    name: 'cossetclothing.com',
    url: 'https://www.cossetclothing.com/products/escape-linen-co-ord-set-with-pants-in-rose-pink',
    industry: 'Fashion · Conscious Wear',
    usesIdukkiFor: 'UGC stories widget on every product page',
    widgetType: 'PDP carousel',
    initial: 'C',
    logo: '/brand/customer-logos/cossetclothing.png',
    videos: [clip('cosset-clothing-pdp.webm', 7, 'UGC stories on the product page')],
  },
  {
    slug: 'khojcrafts',
    name: 'khojcrafts.com',
    url: 'https://khojcrafts.com/pages/happy-customers-review-us',
    industry: 'Home · Antique & Decor',
    usesIdukkiFor: 'Instagram UGC wall on happy customers page',
    widgetType: 'Premium UGC wall',
    initial: 'K',
    logo: '/brand/customer-logos/khojcraft.png',
    videos: [clip('khojcraft.webm', 5, 'Instagram UGC wall, happy-customers page')],
  },
  {
    slug: 'beforeever',
    name: 'beforeever.com.au',
    url: 'https://beforeever.com.au/products/shes-the-one-lavender',
    industry: 'Kids · Couture Wear',
    usesIdukkiFor: 'UGC gallery showcasing real kids in couture on PDP',
    widgetType: 'PDP Carousel',
    initial: 'B',
    logo: '/brand/customer-logos/beforeandafter.png',
    videos: [clip('beforeever-pdp.webm', 6, 'UGC gallery on the product page')],
  },
  {
    slug: 'gokyo',
    name: 'gokyo.in',
    url: 'https://gokyo.in/products/k2-survivor-jacket-men?utm_source=IdukkiUGC&utm_medium=IdukkiUGC&utm_campaign=IdukkiU',
    industry: 'Outdoor · Trekking Gear',
    usesIdukkiFor: 'Shoppable UGC wall + customer photo carousel on PDP',
    widgetType: 'PDP Carousel',
    initial: 'G',
    logo: '/brand/customer-logos/gokyo.svg',
    videos: [
      clip('gokyo-homepage.webm', 5, 'Shoppable UGC wall on the homepage'),
      clip('gokyo-pdp.webm', 4, 'Customer photo carousel on the product page'),
    ],
  },
  {
    slug: 'wyo',
    name: 'wyo.in',
    url: 'https://wyo.in/products/green-camo-t-shirt-dress?utm_source=IdukkiUGC&utm_medium=IdukkiUGC&utm_campaign=IdukkiU',
    industry: 'Fashion · Customisable Apparel',
    usesIdukkiFor: 'Shoppable UGC gallery on homepage + PDP',
    widgetType: 'PDP Carousel',
    initial: 'W',
    logo: '/brand/customer-logos/wyo.png',
    videos: [
      clip('wyo-homepage.webm', 5, 'Shoppable UGC gallery on the homepage'),
      clip('wyo-pdp.webm', 2, 'UGC gallery on the product page'),
    ],
  },
  {
    slug: 'sterlingholidays',
    name: 'sterlingholidays.com',
    url: 'https://www.sterlingholidays.com',
    industry: 'Travel · Holiday Resorts',
    usesIdukkiFor: '#SterlingHolidays SNAP-TAG-SHARE inspiration wall',
    widgetType: 'Inspiration wall',
    initial: 'S',
    logo: '/brand/customer-logos/sterlingholidays.png',
    videos: [clip('sterlingholidays-inspiration.webm', 3, '#SterlingHolidays inspiration wall')],
  },
  {
    slug: 'qualitybottles',
    name: 'qualitybottles.co.uk',
    url: 'https://www.qualitybottles.co.uk',
    industry: 'Home · Glass Packaging',
    usesIdukkiFor: '@QualityBottles SNAP-SHARE-GET FEATURED Instagram UGC wall',
    widgetType: 'Inspiration wall',
    initial: 'Q',
    logo: '/brand/customer-logos/qualitybottles.png',
    videos: [clip('qualitybottles-homepage.webm', 5, '@QualityBottles Instagram UGC wall')],
  },
  {
    slug: 'rangkalp',
    name: 'rangkalp.com',
    url: 'https://rangkalp.com/',
    industry: 'Fashion · Ethnic Wear',
    usesIdukkiFor: 'Shoppable Instagram reel carousel on homepage',
    widgetType: 'Reel carousel',
    initial: 'R',
    logo: '/brand/customer-logos/rangkalp.png',
    videos: [clip('rangkalp.webm', 7, 'Shoppable Instagram reel carousel')],
  },
]

export function findStorefront(slug: string) {
  return STOREFRONTS.find((s) => s.slug === slug)
}
